import { formatDanishLongDate } from "@/util/date";
import { AuthorshipEvidenceListModel } from "../models/authorship";
import ClePagination from "./ClePagination";
import CleTable, { type CleTableColumn } from "./CleTable";
import ReportedStatusBadge from "./ReportedStatusBadge";
import { formatLessons } from "../utils/lessonLimits";

export type Authorship = {
  id: string;
  bookTitle: string;
  publisher: string;
  publicationYear: number;
  isbn: string;
  lessons: number | string;
  reported: boolean;
  createdAt: string;
};

type AuthorshipsTableProps = {
  authorships: AuthorshipEvidenceListModel[];
  isError?: boolean;
  isLoading?: boolean;
  onAuthorshipClick?: (authorshipId: string) => void;
  pagination?: {
    isDisabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    totalPages: number;
  };
  showStatusColumn?: boolean;
};

const AuthorshipsTable = ({
  authorships,
  isError = false,
  isLoading = false,
  onAuthorshipClick,
  pagination,
  showStatusColumn = true,
}: AuthorshipsTableProps) => {
  const columns: CleTableColumn<AuthorshipEvidenceListModel>[] = [
    {
      id: "bookTitle",
      header: "Titel",
      title: (authorship) => authorship.bookTitle,
      cell: (authorship) => authorship.bookTitle,
    },
    {
      id: "publisher",
      header: "Forlag",
      cell: (authorship) => authorship.publisher,
    },
    {
      id: "publicationYear",
      header: "Udgivelsesår",
      cell: (authorship) => authorship.publicationYear,
    },
    {
      id: "isbn",
      header: "ISBN",
      cell: (authorship) => authorship.isbn,
    },
    {
      id: "lessons",
      header: "Lektioner",
      className: "text-end",
      cell: (authorship) => formatLessons(authorship.numberOfLessons),
    },
    ...(showStatusColumn
      ? [
          {
            id: "status",
            header: "Status",
            cell: (authorship: AuthorshipEvidenceListModel) => (
              <ReportedStatusBadge reported={authorship.isReported} />
            ),
          },
        ]
      : []),
    {
      id: "createdAt",
      header: "Oprettet",
      cell: (authorship) => formatDanishLongDate(authorship.createdAt),
    },
  ];

  return (
    <>
      <CleTable
        columns={columns}
        data={authorships}
        emptyDescription="Når du opretter forfatterskab, vises det her i oversigten."
        emptyTitle="Ingen forfatterskab oprettet endnu"
        errorMessage="Forfatterskab kunne ikke indlæses lige nu."
        isError={isError}
        isLoading={isLoading}
        rowAction={
          onAuthorshipClick
            ? {
                onClick: (authorshipId) => onAuthorshipClick(authorshipId),
              }
            : undefined
        }
      />
      {pagination != null && authorships.length > 0 ? (
        <ClePagination
          ariaLabel="Forfatterskab pagination"
          isDisabled={pagination.isDisabled}
          itemLabel="forfatterskab"
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      ) : null}
    </>
  );
};

export default AuthorshipsTable;
